import DataLoader from 'dataloader';
import type { JsonLd } from '@atlaskit/json-ld-types/jsonld';
import retry, { type Options } from 'async-retry';
import pThrottle from 'p-throttle';
import {
	type InvokePayload,
	APIError,
	InvalidUrlError,
	type InvocationSearchPayload,
	type EnvironmentsKeys,
	getResolverUrl,
	request,
	NetworkError,
	getStatus,
	type ProductType,
	type CardAppearance,
} from '@atlaskit/linking-common';
import { type CardClient as CardClientInterface } from './types';
import {
	type BatchResponse,
	type SuccessResponse,
	type ErrorResponse,
	isSuccessfulResponse,
	isErrorResponse,
	type SearchProviderInfoResponse,
	type SearchProviderInfo,
} from './types/responses';
import { type ResourcePayload, type ResourceType, type InvokeRequest } from './types/requests';
import { LRUMap } from 'lru_map';
import uniqBy from 'lodash/uniqBy';
import { fg } from '@atlaskit/platform-feature-flags/fg';

const MAX_BATCH_SIZE = 50;
const MIN_TIME_BETWEEN_BATCHES = 250;
const URL_RESPONSE_CACHE_SIZE = 100;

// Contains cached mapping between url and a promise of a response.
// Note that promise can be either unsettled/ongoing OR successfully resolved (not an error or non-resolved)
export const urlResponsePromiseCache: LRUMap<
	string,
	Promise<SuccessResponse | ErrorResponse>
> = new LRUMap<string, Promise<SuccessResponse | ErrorResponse>>(URL_RESPONSE_CACHE_SIZE);

export default class CardClient implements CardClientInterface {
	private resolverUrl: string;
	readonly envKey?: string;
	readonly baseUrlOverride?: string;
	private urlLoadersByDomain: Record<
		string,
		DataLoader<ResourcePayload<'URL'>, SuccessResponse | ErrorResponse>
	>;
	private retryConfig: Options;
	private resolvedCache: Record<string, boolean>;
	private product?: ProductType;
	private headers?: Record<string, string>;

	constructor(envKey?: EnvironmentsKeys, baseUrlOverride?: string) {
		this.resolverUrl = getResolverUrl(envKey, baseUrlOverride);
		this.urlLoadersByDomain = {};
		this.retryConfig = {
			retries: 2,
		};
		this.resolvedCache = {};
		this.envKey = envKey;
		this.baseUrlOverride = baseUrlOverride;
	}

	public setProduct(product: ProductType): void {
		this.product = product;
	}

	/**
	 * Set custom headers to be sent with each request to the resolver.
	 *
	 * @param headers - Object containing headers to be sent with each request.
	 */
	public setHeaders(headers: Record<string, string>): void {
		this.headers = headers;
	}

	private postBatchResolveNew = async <TType extends ResourceType>(
		resources: ReadonlyArray<ResourcePayload<TType>>,
		resourceType: TType,
		keyGetter: (resource: ResourcePayload<TType>) => string,
	): Promise<BatchResponse> => {
		// De-duplicate requested URLs (see `this.createUrlLoader` for more detail).
		// Also de-duplicate requested ARIs as backend does not de-duplicate any requests.
		const deDuplicatedResources = uniqBy(resources, keyGetter);

		let resolvedResources: BatchResponse = [];

		const headers = {
			/**
			 * This header exist to enable the backend to process relative time, eg: "today", with respect to the user timezone.
			 * eg: used in "confluence-object-provider" to resolve confluence SLLV links that may involve relative time calculation.
			 */
			'origin-timezone': Intl?.DateTimeFormat().resolvedOptions().timeZone,

			// send product source as header X-Product if defined
			...(this.product ? { 'X-Product': this.product } : {}),
			// add custom headers if defined
			...(this.headers ?? {}),
		};

		try {
			// Ask the backend to resolve the URLs for us.
			if (resourceType === 'URL') {
				resolvedResources = await request<BatchResponse>(
					'post',
					`${this.resolverUrl}/resolve/batch`,
					deDuplicatedResources,
					headers,
				);
			}
			// Ask the backend to resolve the ARIs for us.
			if (resourceType === 'ARI') {
				resolvedResources = await request<BatchResponse>(
					'post',
					`${this.resolverUrl}/resolve/ari/batch`,
					deDuplicatedResources,
					headers,
				);
			}
		} catch (error) {
			// we make sure we return a valid dataloader response by creating an error
			// response for each url/ari
			resolvedResources = resources.map(() => {
				// @ts-ignore
				const status = isErrorResponse(error) ? error.status : 500;
				const errorResponse: ErrorResponse = {
					status,
					// @ts-ignore
					error,
				};
				return errorResponse;
			});
		}

		// Reduce into a map to make accessing faster and easier.
		const map: Record<string, SuccessResponse | ErrorResponse> = {};
		// NOTE: the batch endpoint returns the URLs/ARIs in the same order they were given.
		for (let i = 0; i < deDuplicatedResources.length; ++i) {
			const resource = deDuplicatedResources[i];
			const data = resolvedResources[i];
			map[keyGetter(resource)] = data;
		}

		// Reconvert list back into the original order in which it was given to us.
		return resources.map((originalResource) => map[keyGetter(originalResource)]);
	};

	private postBatchResolve = async (
		resources: ReadonlyArray<string>,
		resourceType: 'URL' | 'ARI' = 'URL',
	): Promise<BatchResponse> => {
		// De-duplicate requested URLs (see `this.createUrlLoader` for more detail).
		// Also de-duplicate requested ARIs as backend does not de-duplicate any requests.
		const deDuplicatedResources = [...new Set(resources)];
		let resolvedResources: BatchResponse = [];

		const headers = {
			/**
			 * This header exist to enable the backend to process relative time, eg: "today", with respect to the user timezone.
			 * eg: used in "confluence-object-provider" to resolve confluence SLLV links that may involve relative time calculation.
			 */
			'origin-timezone': Intl?.DateTimeFormat().resolvedOptions().timeZone,

			// send product source as header X-Product if defined
			...(this.product ? { 'X-Product': this.product } : {}),
			// add custom headers if defined
			...(this.headers ? this.headers : {}),
		};

		try {
			// Ask the backend to resolve the URLs for us.
			if (resourceType === 'URL') {
				resolvedResources = await request<BatchResponse>(
					'post',
					`${this.resolverUrl}/resolve/batch`,
					deDuplicatedResources.map((resourceUrl) => ({ resourceUrl })),
					headers,
				);
			}
			// Ask the backend to resolve the ARIs for us.
			if (resourceType === 'ARI') {
				resolvedResources = await request<BatchResponse>(
					'post',
					`${this.resolverUrl}/resolve/ari/batch`,
					deDuplicatedResources.map((ari) => ({ ari })),
					headers,
				);
			}
		} catch (error) {
			// we make sure we return a valid dataloader response by creating an error
			// response for each url/ari
			resolvedResources = resources.map(() => {
				// @ts-ignore
				const status = isErrorResponse(error) ? error.status : 500;
				const errorResponse: ErrorResponse = {
					status,
					// @ts-ignore
					error,
				};
				return errorResponse;
			});
		}

		// Reduce into a map to make accessing faster and easier.
		const map: Record<string, SuccessResponse | ErrorResponse> = {};
		// NOTE: the batch endpoint returns the URLs/ARIs in the same order they were given.
		for (let i = 0; i < deDuplicatedResources.length; ++i) {
			const resource = deDuplicatedResources[i];
			const data = resolvedResources[i];
			map[resource] = data;
		}

		// Reconvert list back into the original order in which it was given to us.
		return resources.map((originalResource) => map[originalResource]);
	};

	private batchResolveUrl = async (
		urls: ReadonlyArray<ResourcePayload<'URL'>>,
	): Promise<BatchResponse> => {
		return this.postBatchResolveNew(urls, 'URL', (resource) => resource.resourceUrl);
	};

	// Endpoint for batch resolve ari
	private batchResolveAris = async (aris: ReadonlyArray<string>): Promise<BatchResponse> => {
		return this.postBatchResolve(aris, 'ARI');
	};

	private createUrlLoader() {
		const batchResolveThrottler = pThrottle({
			limit: 1,
			interval: MIN_TIME_BETWEEN_BATCHES,
		});
		const throttledBatchResolve = batchResolveThrottler(this.batchResolveUrl);

		return new DataLoader(
			// We place all calls to `batchResolve` in a limiter so we don't send off several simultaneous batch requests.
			// This is for two reasons:
			//  1: we want to avoid getting rate limited upstream (eg: forge and other APIs)
			//  2: we want to avoid sending out heaps of requests from the client at once
			(urls: ReadonlyArray<ResourcePayload<'URL'>>) => throttledBatchResolve(urls),
			{
				maxBatchSize: MAX_BATCH_SIZE,
				// NOTE: we turn off DataLoader's cache because it doesn't work for our use-case. Consider the following:
				// - a smartlink to a restricted item is resolved to "forbidden" with a "request access button"
				// - the user clicks "request access", and then following the auth prompts and gets access
				// - the frontend now re-renders the smartlink, but due to DataLoader's caching, the previous "forbidden" state is
				//   because the smartlink's URL (which is the cache key) is exactly the same
				//
				// For this reason, we disable DataLoader's cache.
				// This means that URLs will not be de-duplicated by DataLoader, so we perform the de-duplication logic
				// ourselves in `this.batchResolveUrl`.
				cache: false,
			},
		);
	}

	private getUrlLoader(hostname: string) {
		if (!this.urlLoadersByDomain[hostname]) {
			this.urlLoadersByDomain[hostname] = this.createUrlLoader();
		}

		return this.urlLoadersByDomain[hostname];
	}

	private getHostName(url: string): string {
		try {
			return new URL(url).hostname;
		} catch (error) {
			throw new InvalidUrlError(error);
		}
	}

	private async resolveUrl(url: string, force: boolean = false, appearance?: CardAppearance) {
		const hostname = this.getHostName(url);
		const isInlineOptEnabled =
			appearance !== undefined && fg('platform_smartlink_inline_resolve_optimization');

		// Include appearance in cache key when feature flag is enabled and appearance is specified
		const cacheKey = isInlineOptEnabled ? `${url}|${appearance}` : url;

		let responsePromise: Promise<SuccessResponse | ErrorResponse> | undefined;

		responsePromise = urlResponsePromiseCache.get(cacheKey);
		if (!responsePromise || force) {
			const urlLoader = this.getUrlLoader(hostname);
			responsePromise = urlLoader.load({
				resourceUrl: url,
				ignoreCachedValue: force || undefined,
				// Pass appearance to ORS when feature flag is enabled
				...(isInlineOptEnabled ? { appearance } : {}),
			});
			urlResponsePromiseCache.set(cacheKey, responsePromise);
		}

		let response: SuccessResponse | ErrorResponse;
		try {
			response = await responsePromise;
		} catch (e) {
			// Technically this never happens, since batchResolve handles errors and doesn't throw,
			// But just in case.
			urlResponsePromiseCache.delete(url);
			throw e;
		}

		const isUnresolvedLink =
			!isSuccessfulResponse(response) || getStatus(response.body) !== 'resolved';
		if (isUnresolvedLink) {
			// We want consequent calls for fetchData() to cause actual http call
			urlResponsePromiseCache.delete(cacheKey);
		}

		return response;
	}

	public async prefetchData(
		url: string,
		appearance?: CardAppearance,
	): Promise<JsonLd.Response | undefined> {
		// 1. Queue the URL as part of a dataloader batch.
		const response = await this.resolveUrl(url, false, appearance);

		if (isSuccessfulResponse(response)) {
			// 2. If the URL resolves, send it back.
			return response.body;
		} else {
			try {
				// 3. If the URL does not resolve, retry it with exponential backoff.
				// This is done so that we avoid the scenario where users are unable to
				// see a resolved Smart Link, when their expectation is to be able to.
				const retriedResponse = await retry(async () => {
					// We check if the link has resolved in the cache and stop trying if so.
					// We do this by triggering an error that the link has already been resolved.
					// This cascades <retryCount> times after which it is handled like a normal
					// 'error', returning `undefined`.
					if (!this.resolvedCache[url]) {
						const response = await this.resolveUrl(url, false, appearance);
						if (isSuccessfulResponse(response)) {
							return response;
						} else {
							throw new Error('Retry for URL failed');
						}
					} else {
						// Short-circuit each of the retries, as described above.
						throw new Error('Retry unneeded - link has been resolved.');
					}
				}, this.retryConfig);

				// Trigger callback after successful backoff.
				return retriedResponse.body;
			} catch {
				// Do nothing in the case of an error - prefetching
				// failures should be silent. Once a link is visible,
				// it will be re-fetched anyhow, in which case a
				// user-facing error is required.
				return undefined;
			}
		}
	}

	private isRateLimitError(response: ErrorResponse | SuccessResponse): response is ErrorResponse {
		return isErrorResponse(response) && response.error.status === 429;
	}

	/**
	 * Fetch data from URL
	 * @param url - The URL to fetch data for
	 * @param force - Whether to force a fresh fetch, bypassing cache
	 * @param appearance - Card appearance hint for ORS to optimize response payload.
	 *                     When 'inline', ORS returns minimal data (title, status).
	 *                     When 'block' or 'embed', ORS returns full data including summary.
	 */
	public async fetchData(
		url: string,
		force?: boolean,
		appearance?: CardAppearance,
	): Promise<JsonLd.Response> {
		let response = await this.resolveUrl(url, force, appearance);

		if (this.isRateLimitError(response)) {
			response = await retry(async () => {
				const retryResponse = await this.resolveUrl(url, false, appearance);
				if (this.isRateLimitError(retryResponse)) {
					throw this.mapErrorResponse(retryResponse, new URL(url).hostname);
				}
				return retryResponse;
			}, this.retryConfig);
		}

		if (!isSuccessfulResponse(response)) {
			throw this.mapErrorResponse(response, new URL(url).hostname);
		}
		// Set a flag in the `resolvedCache` for this URL. The intent of this is
		// to ensure that the exponential backoff method in `prefetchData` does
		// not continue to retry fetching for this URL, especially if it was previously
		// in a failed state. Note: this scenario only occurs on initial page load, if the
		// user scrolls through the page very fast. Once the URL is visible, prefetching
		// no longer takes place.
		this.resolvedCache[url] = true;
		// Return the JSON-LD response back up!
		return response.body;
	}

	/**
	 * Fetch data for multiple ARIs (batch) in one operation
	 * @param aris Array of ARIs to fetch data for
	 */
	public async fetchDataAris(aris: string[]): Promise<BatchResponse> {
		return await this.batchResolveAris(aris);
	}

	public async postData(data: InvokePayload<InvokeRequest>): Promise<JsonLd.Response> {
		const requestData = {
			key: data.key,
			action: data.action,
			context: data.context,
		};
		return await request('post', `${this.resolverUrl}/invoke`, requestData);
	}

	/**
	 * Make request to the Search endpoint See `InvocationRequest` in ORS openapi.yaml for backend
	 * spec.
	 * @param data Payload including the search provider key and query. An empty search query string
	 * results in recent results being returned (pre-query).
	 * @returns JsonLd collection of search results.
	 */
	public async search(data: InvokePayload<InvocationSearchPayload>): Promise<JsonLd.Collection> {
		const { key, action } = data;
		// Note: context in action is different to context in data, see types.
		const { query, context } = action;
		const requestData = {
			key,
			search: {
				query,
				context,
			},
		};
		const response = await request<ErrorResponse | JsonLd.Collection>(
			'post',
			`${this.resolverUrl}/invoke/search`,
			requestData,
		);
		if (isErrorResponse(response)) {
			// There is no hostname/it is not known. Hostname is not logged anyway as it's considered PII.
			throw this.mapErrorResponse(response);
		}
		return response;
	}

	public async fetchAvailableSearchProviders(): Promise<SearchProviderInfo[]> {
		const response = await request<SearchProviderInfoResponse>(
			'post',
			`${this.resolverUrl}/providers`,
			{ type: 'search' },
		);

		return response.providers;
	}

	private mapErrorResponse(response: ErrorResponse, hostname: string = '') {
		// Catch non-200 server responses to fallback or return useful information.
		if (response?.error) {
			const errorType = response.error.type;
			const errorMessage = response.error.message;
			const extensionKey = response.error.extensionKey;
			// this means there was a network error and we fallback to blue link
			// without impacting SLO's
			if (response.error instanceof NetworkError) {
				return new APIError('fallback', hostname, errorMessage, errorType);
			}

			switch (errorType) {
				// BadRequestError - indicative of an API error, render
				// a blue link to mitigate customer impact.
				case 'ResolveBadRequestError':
				case 'SearchBadRequestError':
				case 'BadRequestError':
					return new APIError('fallback', hostname, errorMessage, errorType, extensionKey);
				// AuthError - if the user logs in, we may be able
				// to recover. Render an unauthorized card.
				case 'ResolveAuthError':
				case 'SearchAuthError':
				case 'AuthError':
					return new APIError('auth', hostname, errorMessage, errorType, extensionKey);
				// UnsupportedError - we do not know how to render this URL.
				// Bail out and ask the Editor to render as a blue link.
				case 'ResolveUnsupportedError': // URL isn't supported
				case 'SearchUnsupportedError': // Search isn't supported
				case 'UnsupportedError': // URL isn't supported
					return new APIError('fatal', hostname, errorMessage, errorType, extensionKey);
				case 'ResolveFailedError': // Failed
				case 'SearchFailedError':
				case 'ResolveTimeoutError': // Timeouts
				case 'SearchTimeoutError':
				case 'SearchRateLimitError': //Rate Limit Error
				case 'ResolveRateLimitError':
				case 'InternalServerError': // ORS failures
				case 'TimeoutError':
				case 'RateLimitError':
					return new APIError('error', hostname, errorMessage, errorType, extensionKey);
			}
		}
		// Catch all: we don't know this error, bail out.
		const { error, ...rest } = response || {};
		return new APIError(
			'fatal',
			hostname,
			// reason we stringify the error differently is because
			// JSON.stringify will return "{}" if it is an instance of Error
			// e.g. JSON.stringify(new Error('something went wrong')) will return '{}'
			// reason is JSON.stringify method only serializes enumerable properties but properties like "message" and "stack" are non enumerable
			// e.g. https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Error/message
			// more info here https://stackoverflow.com/questions/18391212/is-it-not-possible-to-stringify-an-error-using-json-stringify
			// we assume 'rest'  to "serializable" meaning all its properties are enumerable
			response ? `${this.stringifyError(error)} ${JSON.stringify(rest)}` : 'Response undefined',
			'UnexpectedError',
		);
	}

	/**
	 * This method will strigify both enumerable and  non enumerable properties of an object
	 * @param err
	 * @returns object serialized to JSON with both enumerable and non enumerable objects
	 */
	private stringifyError(err: object) {
		/**
		 * By default JSON.stringify only serializes enumerable properties of an object. In order for us to
		 * serialize non enumerable properties out of an error we need to expclitily provide the non enumerable property names.
		 * the second parameter to JSON.stringify gets the properties that will be serialized from the object passed in the first argument
		 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/getOwnPropertyNames
		 */
		return JSON.stringify(
			err,
			//In ES5, if the argument to getOwnPropertyNames method is not an object (a primitive), then it will cause a TypeError
			typeof err === 'object' ? Object.getOwnPropertyNames(err) : undefined,
		);
	}
}
